import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Delete, Edit, Visibility, Image as ImageIcon, Notes, AutoAwesome, LocalShipping, Payment } from "@mui/icons-material";
import { useProducts } from "../../hooks/useProducts";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import {
  putConversationCart,
  postConversationPaymentIntent,
  getConversationShippingOptions,
  putConversationShipping,
} from "../../api/client";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value || 0));

function catalogCoverUrl(product) {
  const g = product?.galeria;
  if (!Array.isArray(g) || !g.length) return null;
  const c = g.find((x) => x?.isCover) || g[0];
  const u = c?.url || c?.src;
  return u && String(u).trim() ? String(u).trim() : null;
}

const Products = ({ selectedConversation, onRefetchConversationDetails }) => {
  const conversationId = selectedConversation?.id;
  const projectId = selectedConversation?.projectId;

  const productParams = projectId ? { ids_projetos: String(projectId) } : {};
  const { data: availableProducts, isLoading: loadingCatalog } = useProducts(1, 500, "", productParams);
  const { data: paymentData } = usePaymentMethods(1, 100, "", productParams);
  const paymentMethods = useMemo(
    () => (paymentData?.paymentMethods || []).filter((m) => m.status == null || Number(m.status) === 1),
    [paymentData]
  );

  const [products, setProducts] = useState([]);
  const [productDetails, setProductDetails] = useState({
    lineId: null,
    productId: "",
    nome: "",
    quantidade: 1,
    valor: 0,
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [shipZip, setShipZip] = useState("");
  const [shipLoading, setShipLoading] = useState(false);
  const [shipOptions, setShipOptions] = useState([]);
  const [selectedShip, setSelectedShip] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [appmaxPixDocument, setAppmaxPixDocument] = useState("");

  useEffect(() => {
    const cep = String(selectedConversation?.cep || "").replace(/\D/g, "").slice(0, 8);
    setShipZip(cep);
  }, [selectedConversation?.id, selectedConversation?.cep]);

  useEffect(() => {
    if (!selectedConversation?.products || !Array.isArray(selectedConversation.products)) {
      setProducts([]);
      return;
    }
    const normalized = selectedConversation.products.map((p) => ({
      id: p.id,
      productId: p.productId ?? p.product_id ?? null,
      nome: p.nome ?? p.name ?? "",
      quantidade: p.quantidade ?? p.quantity ?? 1,
      valor: p.valor ?? p.value ?? 0,
      coverImageUrl: p.coverImageUrl ?? null,
      sku: p.sku ?? null,
    }));
    setProducts(normalized);
  }, [selectedConversation?.id, selectedConversation?.products]);

  const persistCart = useCallback(
    async (list) => {
      if (!conversationId) return;
      const items = list
        .filter((p) => p.productId)
        .map((p) => ({
          productId: p.productId,
          quantity: p.quantidade,
          unitPrice: p.valor,
        }));
      if (items.length !== list.length) {
        // eslint-disable-next-line no-alert
        alert("Cada item precisa estar vinculado a um produto do catálogo (selecione na lista).");
        return;
      }
      setSaving(true);
      try {
        await putConversationCart(conversationId, items);
        if (typeof onRefetchConversationDetails === "function") {
          await onRefetchConversationDetails();
        }
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e?.message || "Erro ao salvar produtos no protocolo.");
      } finally {
        setSaving(false);
      }
    },
    [conversationId, onRefetchConversationDetails]
  );

  const total = useMemo(
    () =>
      products.reduce(
        (sum, product) =>
          sum + (Number(product.quantidade) || 0) * (Number(product.valor) || 0),
        0
      ),
    [products]
  );

  const composeToChat = (detail) => {
    if (!conversationId) return;
    window.dispatchEvent(
      new CustomEvent("xpro-chat-compose", {
        detail: { conversationId: Number(conversationId), ...detail },
      })
    );
  };

  const handleSaveProduct = async () => {
    const qtd = Number(productDetails.quantidade);
    const val = Number(productDetails.valor);
    const pid = Number(productDetails.productId);

    if (!pid || !productDetails.nome || qtd <= 0 || val < 0) {
      // eslint-disable-next-line no-alert
      alert("Selecione um produto do catálogo e preencha quantidade e valor.");
      return;
    }

    let updatedProducts;
    if (productDetails.lineId != null) {
      updatedProducts = products.map((p) =>
        p.id === productDetails.lineId
          ? {
              ...p,
              productId: pid,
              nome: productDetails.nome,
              quantidade: qtd,
              valor: val,
            }
          : p
      );
    } else {
      const cat = availableProducts?.products?.find((x) => x.id === pid);
      updatedProducts = [
        ...products,
        {
          id: `local-${Date.now()}`,
          productId: pid,
          nome: productDetails.nome,
          quantidade: qtd,
          valor: val,
          coverImageUrl: catalogCoverUrl(cat),
          sku: cat?.sku ?? null,
        },
      ];
    }

    setProducts(updatedProducts);
    setProductDetails({ lineId: null, productId: "", nome: "", quantidade: 1, valor: 0 });
    setIsEditing(false);
    await persistCart(updatedProducts);
  };

  const handleEditProduct = (product) => {
    setProductDetails({
      lineId: product.id,
      productId: product.productId || "",
      nome: product.nome,
      quantidade: product.quantidade,
      valor: product.valor,
    });
    setIsEditing(true);
    setIsPopupOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    const updatedProducts = products.filter((product) => product.id !== id);
    setProducts(updatedProducts);
    await persistCart(updatedProducts);
  };

  const handleAddProduct = () => {
    setProductDetails({ lineId: null, productId: "", nome: "", quantidade: 1, valor: 0 });
    setIsEditing(true);
    setIsPopupOpen(true);
  };

  const previewProducts = useMemo(() => products.slice(0, 3), [products]);

  const handleQuoteShipping = async () => {
    if (!conversationId || shipZip.length !== 8) {
      // eslint-disable-next-line no-alert
      alert("Informe o CEP com 8 dígitos.");
      return;
    }
    setShipLoading(true);
    setShipOptions([]);
    setSelectedShip(null);
    try {
      const data = await getConversationShippingOptions(conversationId, shipZip);
      const opts = data?.options || [];
      setShipOptions(opts);
      if (!opts.length) {
        // eslint-disable-next-line no-alert
        alert("Nenhuma opção de frete para este CEP. Verifique métodos de entrega e CEPs permitidos.");
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Erro ao calcular frete.");
    } finally {
      setShipLoading(false);
    }
  };

  const handleApplyShipping = async () => {
    if (!conversationId || !selectedShip?.deliveryMethodId) {
      // eslint-disable-next-line no-alert
      alert("Selecione uma opção de frete.");
      return;
    }
    setSaving(true);
    try {
      await putConversationShipping(conversationId, {
        deliveryMethodId: selectedShip.deliveryMethodId,
        freightValue: selectedShip.price,
      });
      if (typeof onRefetchConversationDetails === "function") {
        await onRefetchConversationDetails();
      }
      // eslint-disable-next-line no-alert
      alert("Frete aplicado ao pedido.");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Erro ao salvar frete.");
    } finally {
      setSaving(false);
    }
  };

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((m) => String(m.id) === String(paymentMethodId)),
    [paymentMethods, paymentMethodId]
  );
  const selectedTipo = selectedPaymentMethod != null ? Number(selectedPaymentMethod.tipo) : null;

  const handlePaymentIntent = async () => {
    if (!conversationId || !paymentMethodId) {
      // eslint-disable-next-line no-alert
      alert("Selecione uma forma de pagamento.");
      return;
    }
    if (selectedTipo === 12) {
      const d = String(appmaxPixDocument || "").replace(/\D/g, "");
      if (d.length !== 11) {
        // eslint-disable-next-line no-alert
        alert("Para o método AppMax PIX informe o CPF do pagador (11 dígitos).");
        return;
      }
    }
    setPaymentLoading(true);
    try {
      const body = {
        paymentMethodId: Number(paymentMethodId),
      };
      if (selectedTipo === 12) {
        body.pixDocument = String(appmaxPixDocument || "").replace(/\D/g, "");
      }
      const out = await postConversationPaymentIntent(conversationId, body);
      const lines = [
        `Pedido: ${out.orderCode}`,
        `Valor: ${formatCurrency(out.amount)}`,
        out.paymentMethodName ? `Forma: ${out.paymentMethodName}` : null,
        out.paymentUrl ? `Link de pagamento: ${out.paymentUrl}` : null,
        out.sandboxPaymentUrl && !out.paymentUrl ? `Link (sandbox): ${out.sandboxPaymentUrl}` : null,
        out.copyPaste ? `PIX copia-e-cola: ${out.copyPaste}` : null,
        out.qrCodeUrl ? `QR (URL): ${out.qrCodeUrl}` : null,
        out.note ? `\n${out.note}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      // eslint-disable-next-line no-alert
      alert(lines);
      composeToChat({ appendText: lines });
      const urlToOpen = out.paymentUrl || out.sandboxPaymentUrl;
      if (urlToOpen && typeof window !== "undefined") {
        const open = window.confirm("Abrir o link de pagamento em uma nova aba?");
        if (open) window.open(urlToOpen, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Erro ao gerar intenção de pagamento.");
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} p={1}>
      {products.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nenhum produto vinculado a este protocolo.
        </Typography>
      ) : (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {previewProducts.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 14,
                gap: 1,
              }}
            >
              <Avatar
                variant="rounded"
                src={p.coverImageUrl || undefined}
                sx={{ width: 40, height: 40 }}
              >
                {(p.nome || "?").slice(0, 1)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={p.nome}>
                  {p.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Qtde: {p.quantidade} · {formatCurrency(p.valor)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency((Number(p.quantidade) || 0) * (Number(p.valor) || 0))}
              </Typography>
            </Box>
          ))}
          {products.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{products.length - 3} item(s). Clique em &quot;Ver detalhes&quot; para ver todos.
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="subtitle1" fontWeight="bold" textAlign="right" sx={{ mt: 1 }}>
        Total: {formatCurrency(total)}
      </Typography>

      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={1} flexWrap="wrap">
        <Button
          size="small"
          variant="text"
          startIcon={<Visibility fontSize="small" />}
          onClick={() => setIsPopupOpen(true)}
          disabled={products.length === 0}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Ver detalhes
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={saving ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
          onClick={handleAddProduct}
          disabled={saving || !conversationId}
          sx={{ textTransform: "none", borderRadius: 999, px: 1.8, fontWeight: 500 }}
        >
          Adicionar
        </Button>
      </Box>

      <Divider />

      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Frete (mesmos métodos do e-commerce)
      </Typography>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          label="CEP"
          value={shipZip}
          onChange={(e) => setShipZip(e.target.value.replace(/\D/g, "").slice(0, 8))}
          sx={{ width: 140 }}
        />
        <Button size="small" variant="outlined" onClick={handleQuoteShipping} disabled={shipLoading}>
          Calcular
        </Button>
      </Box>
      {shipOptions.length > 0 && (
        <List dense disablePadding sx={{ maxHeight: 160, overflow: "auto" }}>
          {shipOptions.map((o) => (
            <ListItemButton
              key={`${o.code || o.deliveryMethodId}-${o.name}`}
              selected={selectedShip?.code === o.code}
              onClick={() => setSelectedShip(o)}
            >
              <ListItemText primary={o.name} secondary={`${formatCurrency(o.price)} · ${o.etaLabel || ""}`} />
            </ListItemButton>
          ))}
        </List>
      )}
      <Button
        size="small"
        variant="contained"
        startIcon={<LocalShipping />}
        onClick={handleApplyShipping}
        disabled={saving || !selectedShip}
        sx={{ textTransform: "none" }}
      >
        Aplicar frete ao pedido
      </Button>

      <Divider />

      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Pagamento (atendente)
      </Typography>
      <Select
        size="small"
        displayEmpty
        value={paymentMethodId}
        onChange={(e) => setPaymentMethodId(e.target.value)}
        fullWidth
      >
        <MenuItem value="">Forma de pagamento</MenuItem>
        {paymentMethods.map((m) => (
          <MenuItem key={m.id} value={String(m.id)}>
            {m.nome || `Método #${m.id}`}
          </MenuItem>
        ))}
      </Select>
      {selectedTipo === 12 && (
        <TextField
          size="small"
          label="CPF do pagador (AppMax PIX)"
          value={appmaxPixDocument}
          onChange={(e) => setAppmaxPixDocument(e.target.value)}
          fullWidth
          placeholder="00000000000"
          sx={{ mt: 0.5 }}
        />
      )}
      {selectedTipo === 13 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Método AppMax cartão: a cobrança exige enviar dados do cartão no corpo da API (<code>card</code>). Este painel
          não coleta cartão; use integração própria ou e-commerce.
        </Typography>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={paymentLoading ? <CircularProgress size={14} /> : <Payment />}
        onClick={handlePaymentIntent}
        disabled={paymentLoading || !paymentMethodId}
        sx={{ textTransform: "none" }}
      >
        Gerar referência / copiar para o chat
      </Button>

      <Dialog
        open={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setIsEditing(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Produtos</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!isEditing && (
            <Box sx={{ mt: 2 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Qtd</TableCell>
                      <TableCell align="right">Unit.</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Nenhum produto neste protocolo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product) => (
                        <TableRow key={product.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar variant="rounded" src={product.coverImageUrl || undefined} sx={{ width: 36, height: 36 }}>
                                {(product.nome || "?").slice(0, 1)}
                              </Avatar>
                              <Typography variant="body2">{product.nome}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{product.quantidade}</TableCell>
                          <TableCell align="right">{formatCurrency(product.valor)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(
                              (Number(product.quantidade) || 0) * (Number(product.valor) || 0)
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" gap={0.5} flexWrap="wrap">
                              <Tooltip title="Inserir resumo no campo de mensagem">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    composeToChat({
                                      appendText: `${product.nome}\n${formatCurrency(product.valor)} un. · ${product.quantidade} un.\n${product.sku ? `SKU: ${product.sku}\n` : ""}`.trim(),
                                    })
                                  }
                                >
                                  <Notes fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {product.coverImageUrl ? (
                                <Tooltip title="Carregar foto no envio (se o canal aceitar imagem)">
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() =>
                                      composeToChat({
                                        imageUrl: product.coverImageUrl,
                                        appendText: product.nome,
                                      })
                                    }
                                  >
                                    <ImageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              <Tooltip title="Usar este item na próxima sugestão de IA">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    composeToChat({
                                      focusLineItemIds: [Number(product.id)].filter((n) => !Number.isNaN(n)),
                                    })
                                  }
                                >
                                  <AutoAwesome fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton size="small" color="primary" onClick={() => handleEditProduct(product)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remover">
                                <IconButton size="small" color="error" onClick={() => handleDeleteProduct(product.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {products.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold" }}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(total)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddProduct}
                  sx={{ textTransform: "none", borderRadius: 999, px: 2 }}
                >
                  Adicionar produto
                </Button>
              </Box>
            </Box>
          )}

          {isEditing && (
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                {productDetails.lineId != null ? "Editar produto" : "Adicionar produto"}
              </Typography>
              {loadingCatalog && (
                <Typography variant="caption" color="text.secondary">
                  Carregando catálogo…
                </Typography>
              )}
              <Select
                value={productDetails.productId === "" ? "" : String(productDetails.productId)}
                onChange={(e) => {
                  const pid = e.target.value;
                  const selectedProduct = availableProducts?.products?.find((p) => String(p.id) === String(pid));
                  setProductDetails((prev) => ({
                    ...prev,
                    productId: pid === "" ? "" : Number(pid),
                    nome: selectedProduct?.nome || prev.nome,
                    valor: selectedProduct?.preco != null ? Number(selectedProduct.preco) : prev.valor,
                  }));
                }}
                displayEmpty
                fullWidth
              >
                <MenuItem value="" disabled>
                  Selecione um produto
                </MenuItem>
                {(availableProducts?.products || []).map((product) => {
                  const thumb = catalogCoverUrl(product);
                  return (
                    <MenuItem key={product.id} value={String(product.id)}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <Avatar variant="rounded" src={thumb || undefined} sx={{ width: 32, height: 32 }}>
                          {(product.nome || "?").slice(0, 1)}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" noWrap>
                            {product.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(product.preco)}
                            {product.sku ? ` · SKU ${product.sku}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>

              <TextField
                label="Quantidade"
                type="number"
                inputProps={{ min: 1 }}
                value={productDetails.quantidade || 1}
                onChange={(e) =>
                  setProductDetails((prev) => ({
                    ...prev,
                    quantidade: Number(e.target.value) || 1,
                  }))
                }
                fullWidth
              />

              <TextField
                label="Valor unitário (R$)"
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
                value={productDetails.valor}
                onChange={(e) =>
                  setProductDetails((prev) => ({
                    ...prev,
                    valor: Number(e.target.value) || 0,
                  }))
                }
                fullWidth
              />

              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(
                    (Number(productDetails.quantidade) || 0) * (Number(productDetails.valor) || 0)
                  )}
                </Typography>
              </Box>

              <Box display="flex" gap={2} mt={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setIsEditing(false);
                    setProductDetails({ lineId: null, productId: "", nome: "", quantidade: 1, valor: 0 });
                  }}
                >
                  Cancelar
                </Button>
                <Button variant="contained" fullWidth onClick={handleSaveProduct} disabled={saving}>
                  Salvar
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!isEditing && (
            <Button
              onClick={() => {
                setIsPopupOpen(false);
                setIsEditing(false);
              }}
            >
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
